import { forwardRef, useState, useMemo } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Paper,
  ActionIcon,
  ScrollArea,
} from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { Card } from '../../ui';
import { FadeIn, HoverScale } from '../../animations';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface TimeSlot {
  time: string; // HH:mm format
  available: boolean;
  label?: string;
}

export interface TimeSlotPickerProps {
  slots?: TimeSlot[];
  selectedSlot?: string;
  selectedDate?: Date;
  onSlotSelect?: (slot: string, date: Date) => void;
  onDateChange?: (date: Date) => void;
  duration?: number; // in minutes
  startHour?: number;
  endHour?: number;
  interval?: number; // in minutes
  bookedSlots?: { date: Date; time: string }[];
  showWeekView?: boolean;
  loading?: boolean;
  className?: string;
}

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const TimeSlotPicker = forwardRef<HTMLDivElement, TimeSlotPickerProps>(
  (
    {
      slots,
      selectedSlot,
      selectedDate = new Date(),
      onSlotSelect,
      onDateChange,
      duration = 30,
      startHour = 9,
      endHour = 17,
      interval = 30,
      bookedSlots = [],
      showWeekView = true,
      loading = false,
      className,
    },
    ref
  ) => {
    const [currentDate, setCurrentDate] = useState(selectedDate);
    const [activeDate, setActiveDate] = useState(selectedDate);
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    // Generate time slots if not provided
    const generatedSlots = useMemo(() => {
      if (slots) return slots;

      const result: TimeSlot[] = [];
      for (let hour = startHour; hour < endHour; hour++) {
        for (let minute = 0; minute < 60; minute += interval) {
          const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          const isBooked = bookedSlots.some(
            (slot) =>
              slot.time === time &&
              slot.date.toDateString() === activeDate.toDateString()
          );
          result.push({
            time,
            available: !isBooked,
            label: formatTimeLabel(hour, minute),
          });
        }
      }
      return result;
    }, [slots, startHour, endHour, interval, bookedSlots, activeDate]);

    function formatTimeLabel(hour: number, minute: number): string {
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
    }

    const getWeekDates = useMemo(() => {
      const dates: Date[] = [];
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());

      for (let i = 0; i < 7; i++) {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        dates.push(date);
      }
      return dates;
    }, [currentDate]);

    const handlePrevWeek = () => {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() - 7);
      setCurrentDate(newDate);
    };

    const handleNextWeek = () => {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + 7);
      setCurrentDate(newDate);
    };

    const handleDateSelect = (date: Date) => {
      setActiveDate(date);
      onDateChange?.(date);
    };

    const handleSlotSelect = (time: string) => {
      onSlotSelect?.(time, activeDate);
    };

    const isToday = (date: Date): boolean => {
      const today = new Date();
      return date.toDateString() === today.toDateString();
    };

    const isSelected = (date: Date): boolean => {
      return date.toDateString() === activeDate.toDateString();
    };

    const isPast = (date: Date): boolean => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date < today;
    };

    return (
      <FadeIn>
        <Card ref={ref} className={className}>
          <Stack gap="md">
            {/* Week navigation */}
            {showWeekView && (
              <Box>
                <Group justify="space-between" mb="sm">
                  <ActionIcon variant="subtle" onClick={handlePrevWeek}>
                    <IconChevronLeft size={18} />
                  </ActionIcon>
                  <Text fw={500} style={accessibilityStyles}>
                    {currentDate.toLocaleDateString(undefined, {
                      month: 'long',
                      year: 'numeric',
                    })}
                  </Text>
                  <ActionIcon variant="subtle" onClick={handleNextWeek}>
                    <IconChevronRight size={18} />
                  </ActionIcon>
                </Group>

                {/* Week days */}
                <Group gap={4} grow>
                  {getWeekDates.map((date, index) => {
                    const past = isPast(date);
                    const today = isToday(date);
                    const selected = isSelected(date);

                    return (
                      <Paper
                        key={index}
                        p="xs"
                        withBorder={selected}
                        style={{
                          textAlign: 'center',
                          cursor: past ? 'not-allowed' : 'pointer',
                          opacity: past ? 0.5 : 1,
                          backgroundColor: selected
                            ? 'var(--mantine-color-blue-0)'
                            : today
                            ? 'var(--mantine-color-gray-0)'
                            : undefined,
                          borderColor: selected ? 'var(--mantine-color-blue-5)' : undefined,
                        }}
                        onClick={() => !past && handleDateSelect(date)}
                      >
                        <Text size="xs" c="dimmed" style={accessibilityStyles}>
                          {DAYS_OF_WEEK[date.getDay()]}
                        </Text>
                        <Text
                          size="lg"
                          fw={today || selected ? 700 : 400}
                          c={selected ? 'blue' : today ? 'blue.7' : undefined}
                        >
                          {date.getDate()}
                        </Text>
                      </Paper>
                    );
                  })}
                </Group>
              </Box>
            )}

            {/* Time slots */}
            <Box>
              <Text size="sm" fw={500} mb="sm" style={accessibilityStyles}>
                Available times for{' '}
                {activeDate.toLocaleDateString(undefined, {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                })}
              </Text>
              <ScrollArea h={300}>
                <Box
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                    gap: 8,
                  }}
                >
                  {generatedSlots.map((slot) => {
                    const isSlotSelected = selectedSlot === slot.time;

                    return (
                      <HoverScale key={slot.time} scale={1.02}>
                        <Paper
                          p="sm"
                          withBorder
                          style={{
                            textAlign: 'center',
                            cursor: slot.available ? 'pointer' : 'not-allowed',
                            opacity: slot.available ? 1 : 0.5,
                            backgroundColor: isSlotSelected
                              ? 'var(--mantine-color-blue-5)'
                              : slot.available
                              ? undefined
                              : 'var(--mantine-color-gray-1)',
                            borderColor: isSlotSelected ? 'var(--mantine-color-blue-5)' : undefined,
                          }}
                          onClick={() => slot.available && handleSlotSelect(slot.time)}
                        >
                          <Text
                            size="sm"
                            fw={isSlotSelected ? 600 : 400}
                            c={isSlotSelected ? 'white' : undefined}
                          >
                            {slot.label || slot.time}
                          </Text>
                        </Paper>
                      </HoverScale>
                    );
                  })}
                </Box>
              </ScrollArea>
            </Box>

            {/* Duration info */}
            <Text size="xs" c="dimmed" ta="center" style={accessibilityStyles}>
              Each slot is {duration} minutes
            </Text>
          </Stack>
        </Card>
      </FadeIn>
    );
  }
);

TimeSlotPicker.displayName = 'TimeSlotPicker';

export default TimeSlotPicker;
